"use client";

import { useState } from "react";
import PageChange from "@/components/PageChange";
import Image from "next/image";
import Link from "next/link";
import { getS3ImageUrl } from "@/utils/routes";
import {
  getAllBlogs,
  getAllTrendingProducts,
  getBlogsBySlugs,
} from "@/requests/blogs";
import Meta from "@/components/Meta";
import {
  getTokenCookie,
  jsonParseSafe,
  numberWithCommas,
  toLoginPage,
} from "@/utils";
import { QuestionIcon } from "@/components/Icons";
import { useRouter } from "@/hooks/useCompatRouter";
import { setGlobalPopupElement } from "@/store/features/globalSlice";
import PopupManager from "@/components/PopupManager";
import AskQuestionForm from "@/components/AskQuestionForm";
import { useAppDispatch } from "@/store/hooks";
import AskCommentForm from "@/components/Comment";
import CommentSection from "@/components/CommentSection";

const BLogDetails = ({
  blogs,
  trendingProducts,
  recentPosts,
}: {
  blogs: any;
  totalPage?: number;
  trendingProducts: any[];
  recentPosts: any[];
}) => {
  const [page, setPage] = useState<number>(1);
  const [data, setData] = useState<any>(blogs);
  const router = useRouter();
  const dispatch = useAppDispatch();

  const token = getTokenCookie();

  // Optional: Client-side pagination (if API supports it)
  const handlePageChange = async (newPage: number) => {
    const res = await getAllBlogs(
      10,
      newPage,
      "serial_no",
      "ASC",
      "active",
      true
    );
    setData(res.Data || []);
    setPage(res.currentPage || newPage);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleAddComment = () => {
    if (!token) {
      toLoginPage(router);
    } else
      dispatch(
        setGlobalPopupElement(
          <PopupManager>
            <AskCommentForm blogs={blogs} />
          </PopupManager>
        )
      );
  };

  return (
    <>
      <Meta
        title={blogs?.seo_title || blogs.title}
        description={blogs?.seo_description || ""}
        keyword={blogs?.seo_tag ? jsonParseSafe(blogs?.seo_tag) : []}
        image={getS3ImageUrl(blogs?.seo_image || blogs.thamble || "")}
        slug={"blogs/" + blogs?.slug}
      />
      <div className="pb-6">
        <div className="container">
          <div className="w-full bg-gray-50 py-10 min-h-[70vh]">
            <div className="flex flex-col lg:flex-row gap-8">
              {/* ===== Main Blog Area ===== */}
              <div className="w-full lg:w-[70%]">
                <div key={blogs.id} className="transition overflow-hidden">
                  <div className="relative h-[570px] w-full">
                    <Link
                      href={`/blogs/${blogs.slug}`}
                      className="block relative h-full w-full"
                    >
                      <Image
                        src={getS3ImageUrl(blogs.thamble)}
                        alt={blogs.title?? 'blog thamble image'}
                        blurDataURL="https://www.unfe.org/wp-content/uploads/2019/04/SM-placeholder.png"
                        placeholder="blur"
                        fill
                        unoptimized={true}
                        sizes="(max-width: 768px) 100vw, 50vw"
                        priority
                        className="rounded-lg"
                      />
                      <span
                        className={`absolute top-[2%] right-[1%] inline-block px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wide ${
                          blogs.isHot
                            ? "bg-red-500 text-white shadow-lg"
                            : "bg-gray-300 text-gray-800"
                        }`}
                      >
                        {blogs.isHot ? "HOT" : "New"}
                      </span>
                    </Link>
                  </div>

                  <div>
                    <div>
                      <p className="text-sm text-gray-500 my-2">
                        {new Date(blogs.created_at).toLocaleDateString(
                          "en-US",
                          {
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                          }
                        )}
                      </p>
                      {blogs.tags && JSON.parse(blogs.tags).length > 0 && (
                        <div className="mb-2">
                          {JSON.parse(blogs.tags).map(
                            (tag: string, index: number) => (
                              <span
                                key={index}
                                className="inline-block bg-gray-200 text-gray-800 text-xs px-2 py-1 rounded-full mr-2 mb-2"
                              >
                                {tag}
                              </span>
                            )
                          )}
                        </div>
                      )}
                    </div>
                    <Link
                      href={`/blogs/${blogs.slug}`}
                      className="text-lg font-semibold mb-2 hover:text-[#f26e21] transition"
                    >
                      {blogs.title}
                    </Link>
                    <div
                      className="html-content overflow-auto hide-scrollbar"
                      dangerouslySetInnerHTML={{
                        __html: blogs.excerpt,
                      }}
                    ></div>
                    <div
                      className="html-content overflow-auto hide-scrollbar"
                      dangerouslySetInnerHTML={{
                        __html: blogs.content,
                      }}
                    ></div>
                  </div>
                </div>
                <div>
                  <CommentSection data={blogs} handleAddComment={handleAddComment} />
                </div>
                
              </div>

              <div className="w-full lg:w-[30%]">
                {/* Trending Products */}
                <div>
                  <h3 className="text-[28px] font-semibold mb-4">
                    Trending Products
                  </h3>
                  <div className="flex flex-col gap-4 min-h-[300px]">
                    {trendingProducts.map((product) => (
                      <div key={product.id} className="flex gap-3 items-center">
                        <img
                          src={getS3ImageUrl(product.product_image)}
                          alt="recent"
                          className="w-20 h-16"
                        />
                        <div>
                          <Link
                            href={`/product/${product.product_slug}`}
                            className="flex-1"
                          >
                            <h4 className="text-[14px] lg:text-[16px] font-medium hover:text-[#f26e21] transition">
                              {product.product_name}
                            </h4>
                          </Link>
                          <p className={``}>
                            {product.price &&
                              product.discounted_price &&
                              product.discounted_price < product.price &&
                              product.discounted_price > 1 && (
                                <>
                                  <span className="!text-[#F26E21]">
                                    Tk.{" "}
                                    {numberWithCommas(
                                      Math.ceil(product.discounted_price)
                                    )}
                                  </span>
                                  <span className="line-through text-gray-500 ml-2">
                                    <span></span>
                                    Tk.{" "}
                                    {numberWithCommas(Math.ceil(product.price))}
                                  </span>
                                </>
                              )}
                            {product.price &&
                              product.discounted_price &&
                              product.discounted_price >= product.price && (
                                <>
                                  <span className="text-[#F26E21]">
                                    Tk.{" "}
                                    {numberWithCommas(Math.ceil(product.price))}
                                  </span>
                                </>
                              )}
                            {product.price && !product.discounted_price && (
                              <>
                                <span className="!text-[#F26E21]">
                                  Tk.{" "}
                                  {numberWithCommas(Math.ceil(product.price))}
                                </span>
                              </>
                            )}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Recent Posts */}
                <div>
                  <h3 className="text-[28px] font-semibold mb-4">
                    Recent Posts
                  </h3>
                  <div className="flex flex-col gap-4 min-h-[300px]">
                    {recentPosts.map((b) => (
                      <div key={b.id} className="flex gap-3 items-center">
                        <img
                          src={getS3ImageUrl(b.thamble)}
                          alt="recent"
                          className="w-20 h-16"
                        />
                        <Link href={`/blogs/${b.slug}`} className="flex-1">
                          <h4 className="text-[14px] lg:text-[16px] font-medium hover:text-[#f26e21] transition">
                            {b.title}
                          </h4>
                        </Link>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default BLogDetails;

//
// ✅ Server-Side Data Fetching (like your ProductPage)
//
export async function getServerSideProps(context: any) {
  const { slug } = context.params;
  try {
    const blogRes = await getBlogsBySlugs(slug);
    const trendingRes = await getAllTrendingProducts(5, 0,'blog');
    const recentRes = await getAllBlogs(5, 0, "id", "DESC", "active", true);

    return {
      props: {
        blogs: blogRes.Data || {},
        trendingProducts: trendingRes.Data || [],
        recentPosts: recentRes.Data || [],
      },
    };
  } catch (error) {
    console.error("Error fetching blogs:", error);
    return {
      props: {
        blogs: [],
        totalPage: 1,
        trendingProducts: [],
        recentPosts: [],
      },
    };
  }
}
